import React, { useState, useEffect } from "react";
import styles from "./quiz.m.css";
import { fetchQuizQuestion, postQuizanswersApi } from "../../../api";
import { get } from "lodash";
import PropTypes from "prop-types";

const sign = require("jwt-encode");

function mapQuestion(item) {
  return {
    id: item.question_id,
    question: item.question,
    options: item.options.map(opt => ({
      id: opt.id,
      label: opt.text
    }))
  };
}

function QuizCard({ userId, jwtKey, quizType }) {
  // ── Name screen state ────────────────────────────────────────────
  const [name, setName] = useState("");
  const [nameSubmitted, setNameSubmitted] = useState(false);

  // ── Quiz state ───────────────────────────────────────────────────
  const [questions, setQuestions] = useState([]);
  const [groupId, setGroupId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alreadyPlayed, setAlreadyPlayed] = useState(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  // ── Submit state ─────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [totalMarks, setTotalMarks] = useState(null);
  const [ShowBanner, setShowBanner] = useState("");
  const [Fallback, setFallback] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);

  function generateJWT(payload) {
    return sign(payload, jwtKey);
  }

  // ── Fetch questions (after name submitted) ───────────────────────
  useEffect(
    () => {
      if (!userId) return;
      setIsLoading(true);
      setError(null);

      const token = generateJWT({ userId });

      fetchQuizQuestion(token, userId, quizType)
        .then(res => {
          console.log("Quiz question API response:", res);
          const status = get(res, ["data", "status"], false);
          const Banner = get(res, ["data", "banners", "desktop"], "");
          const apiName = get(res, ["data", "name"], "");
          const FallbackImage = get(
            res,
            ["data", "icon"],
            "https://media.vikatan.com/vikatan/2026-03-25/gglp5c6u/Vikatan_Thatha_payment_logo_new.png"
          );

          setFallback(FallbackImage);
          if (!status) {
            setAlreadyPlayed(true);
            setNameSubmitted(true);
            return;
          }

          if (apiName) {
            setName(apiName);
          }

          const raw = get(res, ["data", "data"], []);
          const gId = get(res, ["data", "group_id"], null);
          setShowBanner(Banner);
          setGroupId(gId);
          setQuestions(raw.map(mapQuestion));
        })
        .catch(err => setError(err.message))
        .finally(() => setIsLoading(false));
    },
    [userId] // ← removed nameSubmitted from deps
  );

  // ── Derived state ────────────────────────────────────────────────
  const question = questions[currentIndex];
  const selected = question ? answers[question.id] : null;
  const isAnswered = !!selected;
  const isFirst = currentIndex === 0;
  const isLast = currentIndex === questions.length - 1;

  function handleOptionClick(optionId) {
    if (isSubmitted || isSubmitting) return;
    setAnswers(prev => ({ ...prev, [question.id]: optionId }));
    if (!isLast) {
      setTimeout(() => setCurrentIndex(i => i + 1), 600);
    }
  }

  function handlePrev() {
    if (!isFirst) setCurrentIndex(i => i - 1);
  }

  function handleNext() {
    if (!isLast) setCurrentIndex(i => i + 1);
  }

  function getOptionClass(optionId) {
    if (!isAnswered) return styles["option"];
    if (optionId === selected) return `${styles["option"]} ${styles["option--selected"]}`;
    return styles["option"];
  }

  // ── Submit handler ───────────────────────────────────────────────
  function handleSubmit() {
    if (isSubmitting) return;

    const firstUnanswered = questions.findIndex(q => !answers[q.id]);
    if (firstUnanswered !== -1) {
      setCurrentIndex(firstUnanswered);
      // setSubmitError("Please answer question " + (firstUnanswered + 1) + " before submitting.");
      return;
    }

    setIsSubmitting(true);
    // setSubmitError(null);

    const payload = {
      user_id: userId,
      name: name,
      quiz_type: quizType,
      group_id: groupId,
      answers: questions.map(q => ({
        question_id: q.id,
        option_id: answers[q.id]
      }))
    };

    postQuizanswersApi(generateJWT(payload))
      .then(res => {
        const status = get(res, ["data", "status"], false);
        if (!status) {
          setSubmitError(get(res, ["data", "message"], "Submission failed. Please try again."));
          return;
        }
        setTotalMarks(get(res, ["data", "total_marks"], null));
        setIsSubmitted(true);
        setSubmitted(true);
      })
      .catch(err => setSubmitError(err.message))
      .finally(() => setIsSubmitting(false));
  }

  // ── Name screen ──────────────────────────────────────────────────
  if (!nameSubmitted) {
    return (
      <div className={styles["quiz-card-wrapper"]}>
        <div className={`${styles["quiz-nav-btn"]} ${styles["quiz-nav-prev-btn"]}`} style={{ visibility: "hidden" }} />
        <div className={styles["quiz-card"]}>
          {Fallback && (
            <h2 className={styles["quiz-card__title"]}>
              <img src={Fallback} />
            </h2>
          )}
          <div className={styles["quiz-name-screen"]}>
            <p className={styles["quiz-name-screen__label"]}>Enter your name to begin</p>
            <input
              className={styles["quiz-name-screen__input"]}
              type="text"
              placeholder="Your name"
              value={name}
              onChange={e => {
                const value = e.target.value;
                if (/^[a-zA-Z\u0B80-\u0BFF\s]*$/.test(value)) {
                  setName(value);
                }
              }}
              onKeyDown={e => e.key === "Enter" && name.trim() && setNameSubmitted(true)}
            />
            <button
              className={styles["quiz-submit-btn"]}
              onClick={() => setNameSubmitted(true)}
              disabled={!name.trim()}
            >
              <span>Start Quiz</span>{" "}
              <img src="https://images.assettype.com/vikatan/2026-03-24/ceudk8q0/arrow.png" alt="" />
            </button>
          </div>
        </div>
        <div className={`${styles["quiz-nav-btn"]} ${styles["quiz-nav-next-btn"]}`} style={{ visibility: "hidden" }} />
      </div>
    );
  }

  // ── Loading ──────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className={styles["quiz-card-wrapper"]}>
        <div className={styles["quiz-card"]}>
          <p className={styles["quiz-card__status"]}>Loading...</p>
        </div>
      </div>
    );
  }

  // ── Error ────────────────────────────────────────────────────────
  if (error) {
    return (
      <div className={styles["quiz-card-wrapper"]}>
        <div className={styles["quiz-card"]}>
          <p className={styles["quiz-card__status"]}>Error: {error}</p>
        </div>
      </div>
    );
  }

  // ── Already played ───────────────────────────────────────────────
  if (alreadyPlayed) {
    return (
      <div className={styles["quiz-card-wrapper"]}>
        <div className={`${styles["quiz-card"]} ${styles["quiz-card-played"]}`}>
          {Fallback && (
            <div>
              <img src={Fallback} />
            </div>
          )}
          <p className={`${styles["quiz-card__status"]} ${styles["quiz-card__alreadyplayed"]}`}>
            You have already played this quiz. <span>Please come back later for the next quiz!</span>
          </p>
        </div>
      </div>
    );
  }

  // ── Submitted success ────────────────────────────────────────────
  if (submitted) {
    return (
      <div className={styles["quiz-card-wrapper"]}>
        <div className={styles["quiz-card"]}>
          <div className={styles["quiz-success"]}>
            <div className={styles["success-header"]}>
              <img src="https://images.assettype.com/vikatan/2026-03-24/i63f6070/vikatan-newicon.png" />
              <h2>
                Thank you for <br />
                Participating
              </h2>
            </div>
            {/* <div className={styles["name"]}>{name}</div> */}
            <div>
              <img src="https://images.assettype.com/vikatan/2026-03-25/50vomfbw/thanks.png" />
            </div>
            {/* <div className={styles["quiz-success__icon"]}>
              <img src="https://images.assettype.com/vikatan/2026-03-24/a4wcj9hc/final-tick.png" />
            </div> */}
            <p className={styles["quiz-success__title"]}>Quiz Submitted!</p>
            <p className={styles["quiz-success__sub"]}>Your answers have been recorded.</p>
            {totalMarks !== null && (
              <p className={styles["quiz-success__score"]}>
                Score: {totalMarks} / {questions.length}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── No questions ─────────────────────────────────────────────────
  if (!question) {
    return (
      <div className={styles["quiz-card-wrapper"]}>
        <div className={styles["quiz-card"]}>
          <p className={styles["quiz-card__status"]}>No questions available.</p>
        </div>
      </div>
    );
  }

  // ── Render ───────────────────────────────────────────────────────
  return (
    <div className={styles["quiz-card-wrapper"]}>
      {/* Left arrow */}
      <button
        className={`${styles["quiz-nav-btn"]} ${styles["quiz-nav-prev-btn"]} ${
          isFirst ? styles["quiz-nav-btn--hidden"] : ""
        }`}
        onClick={handlePrev}
        aria-label="Previous question"
      >
        ‹
      </button>

      {/* Card */}
      <div className={styles["quiz-card"]}>
        <h2 className={styles["quiz-card__title_question"]}>
          Participate in Quiz and
          <span>Win exciting prizes*</span>
        </h2>

        {ShowBanner && (
          <div className={styles["quiz-question-img"]}>
            <img src={`${ShowBanner}?format=webp&w=500&dpr=1.0`} />
          </div>
        )}

        <div className={styles["quiz-card__body"]}>
          <div className={styles["quiz-card__question-row"]}>
            <span className={styles["quiz-card__question-number"]}>{currentIndex + 1}</span>
            <p className={styles["quiz-card__question"]}>{question.question}</p>
          </div>

          <div className={styles["quiz-card__options"]}>
            {question.options.map(option => (
              <button
                key={option.id}
                className={getOptionClass(option.id)}
                onClick={() => handleOptionClick(option.id)}
                disabled={isSubmitted || isSubmitting}
              >
                {/* <span className={styles["option__icon"]}>✓</span> */}
                <div className={styles["option__icon"]}>
                  <img
                    src={
                      option.id === selected
                        ? "https://images.assettype.com/vikatan/2026-03-26/rwc030md/tick.png"
                        : "https://images.assettype.com/vikatan/2026-03-24/wl72h784/option-icon.png"
                    }
                  />
                </div>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {/* Progress dots */}
        <div className={styles["quiz-card__dots"]}>
          {questions.map((q, index) => (
            <button
              key={q.id}
              className={`${styles["dot"]} ${index === currentIndex ? styles["dot--active"] : ""}`}
              onClick={() => setCurrentIndex(index)}
              aria-label={`Go to question ${index + 1}`}
            />
          ))}
        </div>

        {/* Submit — only on last question */}
        {isLast && (
          <div className={styles["quiz-card__submit"]}>
            {submitError && <p className={styles["quiz-card__submit-error"]}>{submitError}</p>}
            <button className={styles["quiz-submit-btn"]} onClick={handleSubmit} disabled={isSubmitted || isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Quiz"}
            </button>
          </div>
        )}
      </div>

      {/* Right arrow */}
      <button
        className={`${styles["quiz-nav-btn"]} ${styles["quiz-nav-next-btn"]} ${
          isLast ? styles["quiz-nav-btn--hidden"] : ""
        }`}
        onClick={handleNext}
        aria-label="Next question"
      >
        ›
      </button>
    </div>
  );
}

export default QuizCard;

QuizCard.propTypes = {
  userId: PropTypes.string,
  jwtKey: PropTypes.string,
  quizType: PropTypes.string
};

QuizCard.defaultProps = {
  quizType: "t20_quiz"
};
